// @ts-nocheck
/**
 * Cold Water Network Editor — Production Editor (Cold Water)
 *
 * Éditeur de PRODUCTION pour réseau d'eau froide (variante cold-water de production-editor).
 * Switch dynamique entre 7 centrales (CBER, CAuber, CEtoile, CPrintemps, CCanada, CTokyo, CBNF).
 *
 * Kit : production-editor (même contexte que hot-water, différents assets)
 * 
 * Fonctionnalités :
 * - Canvas React Flow avec visualisation du réseau de production
 * - Sidebar avec centrales cliquables → change le graphe affiché
 * - Composants drag-and-drop organisés par sections
 * - Édition de paramètres via modale latérale custom
 * - Suppression d'assets avec confirmation
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    NJAccordion,
    NJAccordionItem,
    NJAccordionItemContent,
    NJAccordionItemHeader,
    NJButton,
    NJFormItem,
    NJInput,
    NJModal,
    NJSelect,
    NJTab,
    NJTabs,
    NJText,
    NJTabPanel,
    NJToastService,
    NJToastContainer,
    NJIcon,
    NJIconButton,
} from '@engie-group/fluid-design-system-react';
import { Panel, useReactFlow, type Node, type Edge } from '@xyflow/react';
import { useForm } from 'react-hook-form';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import {
    ProductionEditorProvider,
    ProductionEditorCanvas,
    useProductionEditor,
    Asset,
    Contract,
    Group,
    GroupMember,
    type IBaseData,
} from '@shared/production-editor';
import type { NodeEditFormData, TabKey, GroupEditFormData, CondenserCoolingType } from './types';
import { useDeleteNode, useUpdateNode } from './hooks';
import { MOCK_PLANTS } from './mocks';
import { buildGraphForPlant, type PlantId, type GroupConfigs } from './graphs';
import { AnimatedFlowEdge } from './AnimatedEdge';
import { AnimatedFlowCyan } from './AnimatedFlowCyan';
import './flow-animation.css';

// Import des icônes SVG depuis le kit production-editor
import departureIcon from '@shared/production-editor/icons/departure.svg';
import returnIcon from '@shared/production-editor/icons/return.svg';
import chillerIcon from '@shared/production-editor/icons/chiller.svg';
import pumpIcon from '@shared/production-editor/icons/pump.svg';
import waterIcon from '@shared/production-editor/icons/water.svg';
import powerIcon from '@shared/production-editor/icons/power.svg';
import waterCoolingIcon from '@shared/production-editor/icons/water_cooling.svg';

// ── Node Types (utiliser les nodes standards du kit, comportement au clic) ──
const CUSTOM_NODE_TYPES = {
    asset: Asset,
    contract: Contract,
    group: Group,
    groupMember: GroupMember,
};

// ── Edge Types (edge animé pour flux) ──
const CUSTOM_EDGE_TYPES = {
    animatedFlow: AnimatedFlowEdge,
    animatedFlowCyan: AnimatedFlowCyan,
};

// ── Meta ────────────────────────────────────────────────────────────────

export const meta = {
    title: 'Éditeur de Production — Eau Froide (7 Centrales)',
    description:
        'Éditeur de production pour réseau d\'eau froide avec 7 centrales (CBER, CAuber, CEtoile, CPrintemps, CCanada, CTokyo, CBNF).',
    tags: ['production-editor', 'cold-water', 'network-editor', 'multi-plant'],
    author: 'stephane.coucke',
    status: 'draft' as const,
};

// ── Navigation Config ───────────────────────────────────────────────────

const navConfig = [
    { label: 'Général',        icon: 'remove_red_eye'   },
    { label: 'Import/export',  icon: 'swap_horiz'       },
    { label: 'Éditeur réseau', icon: 'map'              },
    { label: 'Production',     icon: 'factory'          },
    { label: 'Plannings',      icon: 'insert_invitation' },
    { label: 'Optimisations',  icon: 'stacked_bar_chart' },
];

// ── AppRail Component ───────────────────────────────────────────────────

function AppRail() {
    return (
        <div
            style={{
                width: 60,
                borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '12px 0',
                flexShrink: 0,
            }}
        >
            {/* ENGIE logo */}
            <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--nj-semantic-color-background-brand-primary-default)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, flexShrink: 0,
            }}>
                <span style={{ color: '#fff', fontSize: 14, fontWeight: 700, lineHeight: 1 }}>G</span>
            </div>

            {/* Top: Networks + Calculations + CoreAI */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                {[
                    { icon: 'account_tree',  label: 'Réseaux'    },
                    { icon: 'legend_toggle', label: 'Calculs'     },
                    { icon: 'auto_awesome',  label: 'Nemo CoreAI' },
                ].map((item) => (
                    <div key={item.label} title={item.label} style={{ width: 44, height: 44, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                        <NJIcon name={item.icon} />
                    </div>
                ))}
            </div>

            {/* Bottom utility icons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['history', 'help_outline', 'account_circle', 'power_settings_new'].map((icon, i) => (
                    <div key={icon} style={{ width: 44, height: 44, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--nj-semantic-color-text-neutral-secondary-default)', borderTop: i === 0 ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none', paddingTop: i === 0 ? 2 : 0 }}>
                        <NJIcon name={icon} />
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── NetworkSidebar Component ────────────────────────────────────────────

function NetworkSidebar() {
    return (
        <div
            style={{
                width: 224,
                borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
            }}
        >
            {/* Network name */}
            <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 14, height: 14, borderRadius: 3, background: '#4285f4', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>CENTRE Study v2-Tokyo-cop y2</span>
                </div>
            </div>

            {/* Nav items */}
            <nav style={{ flex: 1 }}>
                {navConfig.map((item) => {
                    const active = item.label === 'Production';
                    return (
                        <div
                            key={item.label}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '11px 14px',
                                borderLeft: active
                                    ? '3px solid var(--nj-semantic-color-border-brand-primary-default)'
                                    : '3px solid transparent',
                                background: active ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent',
                                cursor: 'pointer',
                                color: active
                                    ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                    : 'var(--nj-semantic-color-text-neutral-secondary-default)',
                            }}
                        >
                            <NJIcon name={item.icon} />
                            <span style={{ fontSize: 14, fontWeight: active ? 600 : 400, lineHeight: 1.4 }}>{item.label}</span>
                        </div>
                    );
                })}
            </nav>
        </div>
    );
}

// ── Custom Sidebar with Plant Selector ──────────────────────────────────

interface CustomSidebarProps {
    selectedPlantId: PlantId;
    onPlantChange: (plantId: PlantId) => void;
}

// Centrales visibles : mapper IDs aux noms affichés
const VISIBLE_PLANTS = [
    { id: 'cber' as PlantId, label: 'Bercy froid' },
    { id: 'cauber' as PlantId, label: 'Aubervilliers froid' },
    { id: 'cetoile' as PlantId, label: 'Étoile froid' },
    { id: 'cprintemps' as PlantId, label: 'Printemps froid' },
    { id: 'ccanada' as PlantId, label: 'Canada froid' },
    { id: 'ctokyo' as PlantId, label: 'Tokyo froid' },
    { id: 'cbnf' as PlantId, label: 'BNF froid' },
];

function CustomSidebar({ selectedPlantId, onPlantChange }: CustomSidebarProps) {
    const [centralesOpen, setCentralesOpen] = useState(true);
    const [composantsOpen, setComposantsOpen] = useState(true);

    return (
        <Panel
            position="top-left"
            style={{
                width: 240,
                backgroundColor: 'var(--nj-semantic-color-background-neutral-primary-default)',
                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                borderRadius: '6px',
                boxShadow: '0 4px 16px rgba(15,23,42,0.10)',
                maxHeight: 'calc(100vh - 180px)',
                overflow: 'auto',
            }}
        >
            {/* Centrales accordion */}
            <div style={{ border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', margin: 8, borderRadius: 4, overflow: 'hidden' }}>
                <div
                    onClick={() => setCentralesOpen((o) => !o)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        height: 48,
                        cursor: 'pointer',
                        borderBottom: centralesOpen ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none',
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>Centrales</span>
                    <NJIcon name={centralesOpen ? 'expand_less' : 'expand_more'} />
                </div>
                {centralesOpen && (
                    <>
                        <div>
                            {VISIBLE_PLANTS.map((plant) => {
                                const active = selectedPlantId === plant.id;
                                return (
                                    <div
                                        key={plant.id}
                                        onClick={() => onPlantChange(plant.id)}
                                        style={{
                                            padding: '11px 14px',
                                            background: active ? 'var(--nj-semantic-color-background-brand-secondary-default)' : 'transparent',
                                            borderLeft: active ? '3px solid var(--nj-semantic-color-border-brand-primary-default)' : '3px solid transparent',
                                            fontSize: 14,
                                            fontWeight: active ? 600 : 400,
                                            color: active
                                                ? 'var(--nj-semantic-color-text-brand-primary-default)'
                                                : 'var(--nj-semantic-color-text-neutral-primary-default)',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {plant.label}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ padding: '8px 10px', borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                            <NJButton
                                label="Créer centrale / stockage"
                                emphasis="bold"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Composants accordion */}
            <div style={{ border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', margin: '0 8px 8px', borderRadius: 4, overflow: 'hidden' }}>
                <div
                    onClick={() => setComposantsOpen((o) => !o)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        height: 48,
                        cursor: 'pointer',
                        borderBottom: composantsOpen ? '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' : 'none',
                    }}
                >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--nj-semantic-color-text-neutral-primary-default)' }}>Composants</span>
                    <NJIcon name={composantsOpen ? 'expand_less' : 'expand_more'} />
                </div>
                {composantsOpen && (
                    <div style={{ padding: '12px 14px' }}>
                        {/* Départ/Retour */}
                        <div style={{ marginBottom: 16 }}>
                            <NJText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                Départ/Retour
                            </NJText>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                        borderRadius: 4,
                                        background: '#fff',
                                        cursor: 'grab',
                                    }}
                                >
                                    <img src={departureIcon} alt="Départ" style={{ width: 32, height: 32 }} />
                                </div>
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                        borderRadius: 4,
                                        background: '#fff',
                                        cursor: 'grab',
                                    }}
                                >
                                    <img src={returnIcon} alt="Retour" style={{ width: 32, height: 32 }} />
                                </div>
                            </div>
                        </div>

                        {/* Moyens de production */}
                        <div style={{ marginBottom: 16 }}>
                            <NJText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                Moyens de production
                            </NJText>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div
                                        key={i}
                                        style={{
                                            width: 48,
                                            height: 48,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                            borderRadius: 4,
                                            background: '#fff',
                                            cursor: 'grab',
                                        }}
                                    >
                                        <img src={chillerIcon} alt={`Chiller ${i}`} style={{ width: 32, height: 32 }} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Pompes */}
                        <div style={{ marginBottom: 16 }}>
                            <NJText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                Pompes
                            </NJText>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <div
                                    style={{
                                        width: 48,
                                        height: 48,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                        borderRadius: 4,
                                        background: '#fff',
                                        cursor: 'grab',
                                    }}
                                >
                                    <img src={pumpIcon} alt="Pompe" style={{ width: 32, height: 32 }} />
                                </div>
                            </div>
                        </div>

                        {/* Contrats */}
                        <div>
                            <NJText scale="xs" style={{ display: 'block', marginBottom: 8, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                Contrats
                            </NJText>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {[
                                    { icon: waterIcon, alt: 'Eau' },
                                    { icon: powerIcon, alt: 'Électricité' },
                                    { icon: waterCoolingIcon, alt: 'Eau de refroidissement' },
                                ].map((contract) => (
                                    <div
                                        key={contract.alt}
                                        style={{
                                            width: 48,
                                            height: 48,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                                            borderRadius: 4,
                                            background: '#fff',
                                            cursor: 'grab',
                                        }}
                                    >
                                        <img src={contract.icon} alt={contract.alt} style={{ width: 32, height: 32 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    );
}

// ── Canvas Toolbar Component ────────────────────────────────────────────

function CanvasToolbar({ onSave, autoSaveEnabled }: { onSave: () => void; autoSaveEnabled: boolean }) {
    const { zoomIn, zoomOut, fitView } = useReactFlow();

    return (
        <div style={{ position: 'absolute', top: 12, right: 16, display: 'flex', gap: 6, zIndex: 10 }}>
            {/* Auto-save + save button */}
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: 'var(--nj-semantic-color-background-neutral-primary-default)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <span style={{ padding: '0 10px', fontSize: 12, color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>Auto-save</span>
                <NJIconButton label="Save" icon="save" variant="tertiary" scale="sm" onClick={onSave} />
            </div>
            {/* Zoom controls */}
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: 'var(--nj-semantic-color-background-neutral-primary-default)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <NJIconButton label="Zoom out" icon="remove" variant="tertiary" scale="sm" onClick={() => zoomOut()} />
                <NJIconButton label="Réinitialiser" icon="fit_screen" variant="tertiary" scale="sm" onClick={() => fitView()} />
                <NJIconButton label="Zoom in" icon="add" variant="tertiary" scale="sm" onClick={() => zoomIn()} />
            </div>
            {/* Fullscreen */}
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, background: 'var(--nj-semantic-color-background-neutral-primary-default)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <NJIconButton label="Plein écran" icon="fullscreen" variant="tertiary" scale="sm" />
            </div>
        </div>
    );
}

// ── Canvas Component ────────────────────────────────────────────────────

interface ColdWaterNetworkCanvasProps {
    selectedPlantId: PlantId;
    onPlantChange: (plantId: PlantId) => void;
    groupConfigs: GroupConfigs;
    onGroupConfigChange: (groupId: string, config: CondenserCoolingType) => void;
}

function ColdWaterNetworkCanvas({ selectedPlantId, onPlantChange, groupConfigs, onGroupConfigChange }: ColdWaterNetworkCanvasProps) {
    const { nodes, setNodes } = useProductionEditor();
    const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [deletingNodeId, setDeletingNodeId] = useState<string | null>(null);
    const [condenserType, setCondenserType] = useState<CondenserCoolingType>('parallel');
    const [activeCharacteristicTab, setActiveCharacteristicTab] = useState<string>('pressure');
    const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);
    const [advancedCurveTab, setAdvancedCurveTab] = useState<string>('graphiques');
    const [autoSaveEnabled] = useState(true); // Auto-save toujours activé

    const updateNodeMutation = useUpdateNode();
    const deleteNodeMutation = useDeleteNode();

    // Sauvegarder les positions des nodes dans localStorage (par centrale)
    const saveNodesPosition = useCallback(() => {
        const storageKey = `cold-water-editor-${selectedPlantId}`;
        const nodePositions = nodes.map((node) => ({
            id: node.id,
            position: node.position,
            parentId: node.parentId, // Important pour les nodes enfants de groupes
            width: node.measured?.width,
            height: node.measured?.height,
        }));
        localStorage.setItem(storageKey, JSON.stringify(nodePositions));
        NJToastService('Positions sauvegardées', {
            iconName: 'check_circle',
            dismissAfter: 2000,
        });
    }, [nodes, selectedPlantId]);

    // Auto-save toutes les 30 secondes
    useEffect(() => {
        if (autoSaveEnabled) {
            const interval = setInterval(() => {
                const storageKey = `cold-water-editor-${selectedPlantId}`;
                const nodePositions = nodes.map((node) => ({
                    id: node.id,
                    position: node.position,
                    parentId: node.parentId, // Sauvegarder le parentId pour les nodes enfants
                }));
                localStorage.setItem(storageKey, JSON.stringify(nodePositions));
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [autoSaveEnabled, nodes, selectedPlantId]);

    const { register, handleSubmit, reset } = useForm<NodeEditFormData>();
    const { register: registerGroup, handleSubmit: handleSubmitGroup, reset: resetGroup, setValue: setValueGroup } = useForm<GroupEditFormData>();

    // Trouver le node en édition/suppression
    const editingNode = nodes.find((n) => n.id === editingNodeId);
    const editingGroup = nodes.find((n) => n.id === editingGroupId);
    const deletingNode = nodes.find((n) => n.id === deletingNodeId);

    // Callbacks pour les actions edit/delete - versions stables qui utilisent les états
    const handleNodeEdit = useCallback((nodeId: string) => {
        const node = nodes.find((n) => n.id === nodeId);
        // Vérifier si c'est un groupe (type='group')
        if (node?.type === 'group') {
            // C'est un groupe, ouvrir le formulaire de groupe
            setEditingGroupId(nodeId);
        } else {
            // C'est un node normal, ouvrir le formulaire de node
            setEditingNodeId(nodeId);
        }
    }, [nodes]);

    const handleNodeDelete = useCallback((nodeId: string) => {
        setDeletingNodeId(nodeId);
    }, []);

    // Réinitialiser le formulaire quand un node est sélectionné pour édition
    useEffect(() => {
        if (editingNodeId && editingNode) {
            reset({ label: editingNode.data.label });
            setShowAdvancedOptions(false); // Reset advanced options toggle
        }
    }, [editingNodeId, editingNode, reset]);

    // Réinitialiser le formulaire de groupe quand un groupe est sélectionné
    useEffect(() => {
        if (editingGroupId && editingGroup) {
            const currentConfig = groupConfigs[editingGroupId] || 'parallel';
            setCondenserType(currentConfig);
            // Utiliser setTimeout pour s'assurer que le formulaire est monté
            setTimeout(() => {
                setValueGroup('label', editingGroup.data.label);
            }, 0);
        }
    }, [editingGroupId, editingGroup, groupConfigs, setValueGroup]);

    // Injecter les callbacks dans les nodes avec capture de l'ID
    useEffect(() => {
        setNodes((prevNodes) =>
            prevNodes.map((node) => ({
                ...node,
                data: {
                    ...node.data,
                    onEditClick: () => handleNodeEdit(node.id),
                    onDeleteClick: () => handleNodeDelete(node.id),
                },
            }))
        );
    }, [handleNodeEdit, handleNodeDelete, setNodes, selectedPlantId]);

    // Sauvegarder les modifications
    const onSubmitEdit = useCallback(
        (data: NodeEditFormData) => {
            if (!editingNodeId) return;
            updateNodeMutation.mutate(
                { nodeId: editingNodeId, data },
                {
                    onSuccess: () => {
                        setEditingNodeId(null);
                    },
                }
            );
        },
        [editingNodeId, updateNodeMutation]
    );

    // Sauvegarder les modifications de groupe
    const onSubmitGroup = useCallback(
        (data: GroupEditFormData) => {
            if (!editingGroupId) return;
            // Mettre à jour la configuration du groupe avec l'état condenserType
            onGroupConfigChange(editingGroupId, condenserType);
            // Mettre à jour le label du node si nécessaire
            if (data.label) {
                setNodes((prevNodes) =>
                    prevNodes.map((node) =>
                        node.id === editingGroupId
                            ? { ...node, data: { ...node.data, label: data.label, condenserCoolingType: condenserType } }
                            : node
                    )
                );
            }
            // Sauvegarder les positions après modification pour persister le label
            const storageKey = `cold-water-editor-${selectedPlantId}`;
            const nodePositions = nodes.map((node) => ({
                id: node.id,
                position: node.position,
                parentId: node.parentId,
            }));
            localStorage.setItem(storageKey, JSON.stringify(nodePositions));
            
            setEditingGroupId(null);
            NJToastService('Configuration du groupe mise à jour', {
                iconName: 'check_circle',
                dismissAfter: 3000,
            });
        },
        [editingGroupId, onGroupConfigChange, condenserType, setNodes, nodes, selectedPlantId]
    );

    // Supprimer un nœud
    const handleDeleteNode = useCallback(() => {
        if (!deletingNodeId) return;
        const nodeLabel = deletingNode?.data.label || 'Élément';
        deleteNodeMutation.mutate(deletingNodeId, {
            onSuccess: () => {
                setDeletingNodeId(null);
                NJToastService(`${nodeLabel} supprimé avec succès`, {
                    title: 'Suppression confirmée',
                    iconName: 'check_circle',
                    dismissAfter: 4000,
                });
            },
        });
    }, [deletingNodeId, deletingNode, deleteNodeMutation]);

    return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <ProductionEditorCanvas nodeTypes={CUSTOM_NODE_TYPES} edgeTypes={CUSTOM_EDGE_TYPES} minHeight="100%">
                <CustomSidebar selectedPlantId={selectedPlantId} onPlantChange={onPlantChange} />
                <CanvasToolbar onSave={saveNodesPosition} autoSaveEnabled={autoSaveEnabled} />
            </ProductionEditorCanvas>

            {/* Modale d'édition enrichie (overlay droite) */}
            {editingNodeId && (
                <>
                    {/* Overlay background */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 9998,
                        }}
                        onClick={() => setEditingNodeId(null)}
                    />
                    {/* Panel droite */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '600px',
                            backgroundColor: 'white',
                            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
                            zIndex: 9999,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                    {/* Header */}
                    <div style={{ 
                        padding: '20px 24px',
                        borderBottom: '1px solid var(--nj-core-color-grey-200)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <NJText variant="heading-medium">{editingNode?.data.label || ''}</NJText>
                        <button
                            onClick={() => setEditingNodeId(null)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: '24px',
                                lineHeight: 1,
                                padding: '4px',
                            }}
                        >
                            ×
                        </button>
                    </div>

                    <form onSubmit={handleSubmit(onSubmitEdit)} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                        {/* Body scrollable */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', minHeight: 0 }}>
                            {/* Identifiant */}
                            <NJFormItem label="Identifiant">
                                <NJInput value={editingNode?.id} disabled style={{ backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }} />
                            </NJFormItem>
                            
                            {/* Nom */}
                            <NJFormItem label="Nom *" required style={{ marginTop: '16px' }}>
                                <NJInput 
                                    {...register('label', { required: true, maxLength: 64 })} 
                                    placeholder="Nom de l'entité"
                                />
                                <NJText variant="body-small" style={{ color: 'var(--nj-semantic-color-text-neutral-tertiary-default)', marginTop: '4px', display: 'block' }}>
                                    {editingNode?.data.label?.length || 0}/64
                                </NJText>
                            </NJFormItem>

                            {/* Type (read-only) */}
                            <NJFormItem label="Type" style={{ marginTop: '16px' }}>
                                <NJInput value={editingNode?.data.entityLabel} disabled style={{ backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }} />
                            </NJFormItem>

                            {/* Champs spécifiques Pompe */}
                            {editingNode?.data.entityName === 'VariableSpeedPump' && (
                                <>
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Courbe Caractéristique</NJText>
                                    </div>
                                    
                                    <NJTabs 
                                        label="Courbes"
                                        activeTab={activeCharacteristicTab} 
                                        onClickTabItem={(id) => setActiveCharacteristicTab(id as string)}
                                    >
                                        <NJTab id="pressure" label="Data de pression" ariaControls="panel-pressure" />
                                        <NJTab id="performance" label="Rendement" ariaControls="panel-performance" />
                                    </NJTabs>
                                    
                                    <NJTabPanel id="panel-pressure" isActive={activeCharacteristicTab === 'pressure'}>
                                        <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--nj-semantic-color-surface-subtle)', borderRadius: '8px' }}>
                                            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--nj-core-color-grey-300)', borderRadius: '4px' }}>
                                                <NJText variant="body-default" style={{ color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                                    📊 Courbe de pression
                                                </NJText>
                                            </div>
                                        </div>
                                    </NJTabPanel>
                                    
                                    <NJTabPanel id="panel-performance" isActive={activeCharacteristicTab === 'performance'}>
                                        <div style={{ marginTop: '16px', padding: '16px' }}>
                                            <NJText>Données de rendement...</NJText>
                                        </div>
                                    </NJTabPanel>

                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Paramètres</NJText>
                                    </div>

                                    <NJFormItem label="Vitesse de rotation nominale (rad/s)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.00001" {...register('nominalRotationSpeed')} defaultValue="152.99551" />
                                    </NJFormItem>

                                    <NJFormItem label="Vitesse de rotation minimale (rad/s)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.0001" {...register('minRotationSpeed')} defaultValue="1.0472" />
                                    </NJFormItem>

                                    <NJFormItem label="Débit (m³/h)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('flowRate')} placeholder="Débit volumique" />
                                    </NJFormItem>

                                    <NJFormItem label="Pression (bar)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.1" {...register('pressure')} placeholder="Pression de service" />
                                    </NJFormItem>
                                </>
                            )}

                            {/* Champs spécifiques Chiller */}
                            {editingNode?.data.entityName === 'Chiller' && (
                                <>
                                    {/* Electricity contract */}
                                    <NJFormItem label="Electricity contract" style={{ marginTop: '16px' }}>
                                        <select 
                                            {...register('electricityContract')} 
                                            style={{ 
                                                width: '100%', 
                                                padding: '8px 12px', 
                                                border: '1px solid var(--nj-core-color-grey-300)', 
                                                borderRadius: '4px',
                                                backgroundColor: 'white',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="">Sélectionner...</option>
                                            <option value="contrat-auber">Contrat elec Auber</option>
                                            <option value="contrat-bercy">Contrat elec Bercy</option>
                                            <option value="contrat-canada">Contrat elec Canada</option>
                                            <option value="contrat-dltm">Contrat elec DLTM</option>
                                            <option value="contrat-etoile">Contrat elec Etoile</option>
                                            <option value="contrat-halles">Contrat elec Halles</option>
                                            <option value="contrat-opera">Contrat elec Opera</option>
                                            <option value="contrat-printemps">Contrat elec Printemps</option>
                                            <option value="contrat-tokyo">Contrat elec Tokyo</option>
                                            <option value="contrat-bnf">contrat elec BNF</option>
                                        </select>
                                    </NJFormItem>

                                    <NJFormItem label="Compressor capacity" style={{ marginTop: '16px' }}>
                                        <select 
                                            {...register('compressorCapacity')} 
                                            style={{ 
                                                width: '100%', 
                                                padding: '8px 12px', 
                                                border: '1px solid var(--nj-core-color-grey-300)', 
                                                borderRadius: '4px',
                                                backgroundColor: 'white',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="">Sélectionner...</option>
                                            <option value="site-1">Site 1</option>
                                            <option value="site-2">Site 2</option>
                                            <option value="site-3">Site 3</option>
                                        </select>
                                    </NJFormItem>

                                    <NJFormItem label="Linked exchanger" style={{ marginTop: '16px' }}>
                                        <NJInput {...register('linkedExchanger')} placeholder="Échangeur lié" />
                                    </NJFormItem>

                                    <NJFormItem label="Nominal cooling capacity (kW) *" required style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('nominalCoolingCapacity')} placeholder="275" />
                                    </NJFormItem>

                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Performance / Energy Efficiency Ratio</NJText>
                                    </div>

                                    <NJFormItem label="Nominal EER *" required style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.01" {...register('nominalEER')} placeholder="275" />
                                    </NJFormItem>

                                    <NJFormItem label="Rights efficiency *" required style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.01" {...register('rightsEfficiency')} placeholder="275" />
                                    </NJFormItem>

                                    {/* Evaporator Section */}
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Evaporator</NJText>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <NJFormItem label="Evaporator minimum inlet temp (K) *" required>
                                            <NJInput type="number" {...register('evaporatorMinInletTemp')} placeholder="10" />
                                        </NJFormItem>
                                        <NJFormItem label="Evaporator maximum inlet temp (K) *" required>
                                            <NJInput type="number" {...register('evaporatorMaxInletTemp')} placeholder="10" />
                                        </NJFormItem>
                                    </div>

                                    <NJFormItem label="Evaporator outlet temp (K)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('evaporatorOutletTemp')} placeholder="10" />
                                    </NJFormItem>

                                    <NJFormItem label="Evaporator nominal flow (m³/s)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.01" {...register('evaporatorNominalFlow')} placeholder="10" />
                                    </NJFormItem>

                                    <NJFormItem label="Evaporator control pressure loss (Pa)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('evaporatorControlPressure')} placeholder="10" />
                                    </NJFormItem>

                                    {/* Condenser Section */}
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Condenser</NJText>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <NJFormItem label="Condenser minimum inlet temp (K) *" required>
                                            <NJInput type="number" {...register('condenserMinInletTemp')} placeholder="10" />
                                        </NJFormItem>
                                        <NJFormItem label="Condenser maximum inlet temp (K) *" required>
                                            <NJInput type="number" {...register('condenserMaxInletTemp')} placeholder="10" />
                                        </NJFormItem>
                                    </div>

                                    <NJFormItem label="Condenser outlet temp (K)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('condenserOutletTemp')} placeholder="10" />
                                    </NJFormItem>

                                    <NJFormItem label="Condenser nominal flow (m³/s)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" step="0.01" {...register('condenserNominalFlow')} placeholder="10" />
                                    </NJFormItem>

                                    <NJFormItem label="Condenser control pressure loss (Pa)" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('condenserControlPressure')} placeholder="10" />
                                    </NJFormItem>

                                    {/* Operating constraints */}
                                    <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                        <NJText variant="heading-small">Operating constraints</NJText>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <NJFormItem label="Partial load maximum ratio *" required>
                                            <NJInput type="number" step="0.01" {...register('partialLoadMaxRatio')} placeholder="10" />
                                        </NJFormItem>
                                        <NJFormItem label="Partial load minimum ratio *" required>
                                            <NJInput type="number" step="0.01" {...register('partialLoadMinRatio')} placeholder="10" />
                                        </NJFormItem>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                        <NJFormItem label="Minimum starting *" required>
                                            <NJInput type="number" {...register('minimumStarting')} placeholder="10" />
                                        </NJFormItem>
                                        <NJFormItem label="Maximum starting *" required>
                                            <NJInput type="number" {...register('maximumStarting')} placeholder="10" />
                                        </NJFormItem>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                        <NJFormItem label="Ramp-up electrical power *" required>
                                            <NJInput type="number" {...register('rampUpElectricalPower')} placeholder="10" />
                                        </NJFormItem>
                                        <NJFormItem label="Ramp-up duration *" required>
                                            <NJInput type="number" {...register('rampUpDuration')} placeholder="10" />
                                        </NJFormItem>
                                    </div>

                                    <NJFormItem label="Maximum downtime" style={{ marginTop: '16px' }}>
                                        <NJInput type="number" {...register('maximumDowntime')} placeholder="10" />
                                    </NJFormItem>

                                    {/* Evaluate cooling mode */}
                                    <div style={{ marginTop: '24px' }}>
                                        <NJText variant="body-default" style={{ display: 'block', marginBottom: '8px' }}>
                                            Evaluate cooling mode
                                        </NJText>
                                        <div style={{ display: 'flex', gap: '16px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input type="radio" {...register('evaluateCoolingMode')} value="true" />
                                                <NJText variant="body-default">Yes</NJText>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input type="radio" {...register('evaluateCoolingMode')} value="false" defaultChecked />
                                                <NJText variant="body-default">No</NJText>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Toggle Advanced Options */}
                                    <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={showAdvancedOptions}
                                                onChange={(e) => setShowAdvancedOptions(e.target.checked)}
                                                style={{ width: '18px', height: '18px' }}
                                            />
                                            <NJText variant="body-default" style={{ fontWeight: 500 }}>
                                                Afficher les options avancées
                                            </NJText>
                                        </label>
                                    </div>

                                    {/* Advanced Options Section */}
                                    {showAdvancedOptions && (
                                        <>
                                            <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                                <NJText variant="heading-small">Courbes de Capacité et d'efficacité</NJText>
                                            </div>

                                            <NJTabs 
                                                label="Courbes avancées"
                                                activeTab={advancedCurveTab} 
                                                onClickTabItem={(id) => setAdvancedCurveTab(id as string)}
                                            >
                                                <NJTab id="graphiques" label="Graphiques" ariaControls="panel-graphiques" />
                                                <NJTab id="donnees" label="Données" ariaControls="panel-donnees" />
                                            </NJTabs>

                                            <NJTabPanel id="panel-graphiques" isActive={advancedCurveTab === 'graphiques'}>
                                                <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--nj-semantic-color-surface-subtle)', borderRadius: '8px' }}>
                                                    <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--nj-core-color-grey-300)', borderRadius: '4px' }}>
                                                        <NJText variant="body-default" style={{ color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                                            📊 Graphiques de capacité et efficacité
                                                        </NJText>
                                                    </div>
                                                </div>
                                            </NJTabPanel>

                                            <NJTabPanel id="panel-donnees" isActive={advancedCurveTab === 'donnees'}>
                                                <div style={{ marginTop: '16px' }}>
                                                    {/* Chiller capacity */}
                                                    <NJText variant="body-default" style={{ fontWeight: 500, display: 'block', marginBottom: '12px', color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                                        Chiller capacity
                                                    </NJText>

                                                    <NJFormItem label="Constant *" required style={{ marginTop: '8px' }}>
                                                        <NJInput type="number" {...register('chillerCapacityConstant')} placeholder="10" />
                                                    </NJFormItem>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                                        <NJFormItem label="T_ev_outlet * T_cond_inlet *" required>
                                                            <NJInput type="number" {...register('chillerCapacityTevOutlet')} placeholder="10" />
                                                        </NJFormItem>
                                                        <NJFormItem label="T_ev_outlet * T_cond_inlet *" required>
                                                            <NJInput type="number" {...register('chillerCapacityTcondInlet')} placeholder="10" />
                                                        </NJFormItem>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                                        <NJFormItem label="T_evaporator_outlet *" required>
                                                            <NJInput type="number" {...register('chillerCapacityTevOutlet2')} placeholder="10" />
                                                        </NJFormItem>
                                                        <NJFormItem label="T_evaporator_outlet *2 *" required>
                                                            <NJInput type="number" {...register('chillerCapacityTcondInlet2')} placeholder="10" />
                                                        </NJFormItem>
                                                    </div>

                                                    {/* Full load EER */}
                                                    <NJText variant="body-default" style={{ fontWeight: 500, display: 'block', marginTop: '32px', marginBottom: '12px', color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                                        Full load EER (Energy Efficiency Ratio)
                                                    </NJText>

                                                    <NJFormItem label="Constant *" required style={{ marginTop: '8px' }}>
                                                        <NJInput type="number" {...register('fullLoadEERConstant')} placeholder="10" />
                                                    </NJFormItem>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                                        <NJFormItem label="T_ev_outlet * T_cond_inlet *" required>
                                                            <NJInput type="number" {...register('fullLoadEERTevOutlet')} placeholder="10" />
                                                        </NJFormItem>
                                                        <NJFormItem label="T_ev_outlet * T_cond_inlet *" required>
                                                            <NJInput type="number" {...register('fullLoadEERTcondInlet')} placeholder="10" />
                                                        </NJFormItem>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                                        <NJFormItem label="T_evaporator_outlet *" required>
                                                            <NJInput type="number" {...register('fullLoadEERTevOutlet2')} placeholder="10" />
                                                        </NJFormItem>
                                                        <NJFormItem label="T_condenser_inlet *2 *" required>
                                                            <NJInput type="number" {...register('fullLoadEERTcondInlet2')} placeholder="10" />
                                                        </NJFormItem>
                                                    </div>

                                                    {/* Partial load EER */}
                                                    <NJText variant="body-default" style={{ fontWeight: 500, display: 'block', marginTop: '32px', marginBottom: '12px', color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                                        Partial load EER (Energy Efficiency Ratio)
                                                    </NJText>

                                                    <NJFormItem label="Constant *" required style={{ marginTop: '8px' }}>
                                                        <NJInput type="number" {...register('partialLoadEERConstant')} placeholder="10" />
                                                    </NJFormItem>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                                        <NJFormItem label="PLR (Partial Load Rate)" required>
                                                            <NJInput type="number" {...register('partialLoadEERPLR')} placeholder="10" />
                                                        </NJFormItem>
                                                        <NJFormItem label="PLR*2" required>
                                                            <NJInput type="number" {...register('partialLoadEERPLR2')} placeholder="10" />
                                                        </NJFormItem>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                                        <NJFormItem label="PLR*3" required>
                                                            <NJInput type="number" {...register('partialLoadEERPLR3')} placeholder="10" />
                                                        </NJFormItem>
                                                        <NJFormItem label="T_condenser_inlet*(1-PLR)" required>
                                                            <NJInput type="number" {...register('partialLoadEERTcondInlet')} placeholder="10" />
                                                        </NJFormItem>
                                                    </div>
                                                </div>
                                            </NJTabPanel>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Footer info (création/modification) */}
                            <div style={{ marginTop: '32px', padding: '12px', backgroundColor: 'var(--nj-semantic-color-surface-subtle)', borderRadius: '4px' }}>
                                <NJText variant="body-small" style={{ color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>
                                    Créé par PROTOTYPER le {new Date().toLocaleDateString('fr-FR')}
                                </NJText>
                            </div>
                        </div>

                        {/* Boutons fixes en bas */}
                        <div style={{ 
                            padding: '16px 24px', 
                            borderTop: '1px solid var(--nj-core-color-grey-200)', 
                            display: 'flex', 
                            gap: '12px', 
                            justifyContent: 'flex-end',
                            backgroundColor: 'white',
                            flexShrink: 0
                        }}>
                            <NJButton
                                type="button"
                                variant="secondary"
                                onClick={() => setEditingNodeId(null)}
                            >
                                Annuler
                            </NJButton>
                            <NJButton
                                type="submit"
                                variant="primary"
                                disabled={updateNodeMutation.isPending}
                            >
                                Valider
                            </NJButton>
                        </div>
                    </form>
                </div>
                </>
            )}

            {/* Modale d'édition de groupe (ligne de production) */}
            {editingGroupId && (
                <>
                    {/* Overlay background */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 9998,
                        }}
                        onClick={() => setEditingGroupId(null)}
                    />
                    {/* Panel droite */}
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '600px',
                            backgroundColor: 'white',
                            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
                            zIndex: 9999,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Header */}
                        <div style={{ 
                            padding: '20px 24px',
                            borderBottom: '1px solid var(--nj-core-color-grey-200)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <NJText variant="heading-medium">Configuration : {editingGroup?.data.label || ''}</NJText>
                            <button
                                onClick={() => setEditingGroupId(null)}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '24px',
                                    lineHeight: 1,
                                    padding: '4px',
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={handleSubmitGroup(onSubmitGroup)} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            {/* Body scrollable */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', minHeight: 0 }}>
                                {/* Identifiant */}
                                <NJFormItem label="Identifiant">
                                    <NJInput value={editingGroup?.id} disabled style={{ backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }} />
                                </NJFormItem>
                                
                                {/* Nom */}
                                <NJFormItem label="Nom *" required style={{ marginTop: '16px' }}>
                                    <NJInput 
                                        {...registerGroup('label', { required: true, maxLength: 64 })} 
                                        placeholder="Nom de la ligne de production"
                                    />
                                </NJFormItem>

                                {/* Type (read-only) */}
                                <NJFormItem label="Type" style={{ marginTop: '16px' }}>
                                    <NJInput value={editingGroup?.data.entityLabel} disabled style={{ backgroundColor: 'var(--nj-semantic-color-surface-disabled)' }} />
                                </NJFormItem>

                                {/* Configuration de condenseur */}
                                <div style={{ marginTop: '32px', marginBottom: '16px' }}>
                                    <NJText variant="heading-small">Configuration des groupes froids</NJText>
                                </div>

                                <NJFormItem label="Type de configuration des condenseurs *" required style={{ marginTop: '16px' }}>
                                    <select
                                        value={condenserType}
                                        onChange={(e) => setCondenserType(e.target.value as CondenserCoolingType)}
                                        style={{ 
                                            width: '100%', 
                                            padding: '8px 12px', 
                                            border: '1px solid var(--nj-core-color-grey-300)', 
                                            borderRadius: '4px',
                                            backgroundColor: 'white',
                                            fontSize: '14px',
                                            fontFamily: 'var(--nj-core-font-family-sans)',
                                            cursor: 'pointer',
                                            appearance: 'auto',
                                        }}
                                    >
                                        <option value="parallel">Condenseurs en parallèle</option>
                                        <option value="series">Condenseurs en série</option>
                                    </select>
                                </NJFormItem>

                                <div style={{ 
                                    marginTop: '16px', 
                                    padding: '12px', 
                                    backgroundColor: 'var(--nj-semantic-color-surface-info-subtle)', 
                                    borderRadius: '4px',
                                    border: '1px solid var(--nj-semantic-color-border-info-default)'
                                }}>
                                    <NJText variant="body-small">
                                        <strong>Condenseurs en parallèle :</strong> Les 2 groupes froids fonctionnent en parallèle, sans connexion entre eux.<br/><br/>
                                        <strong>Condenseurs en série :</strong> Les 2 groupes froids sont reliés en série par une ligne bleue.
                                    </NJText>
                                </div>
                            </div>

                            {/* Boutons fixes en bas */}
                            <div style={{ 
                                padding: '16px 24px', 
                                borderTop: '1px solid var(--nj-core-color-grey-200)', 
                                display: 'flex', 
                                gap: '12px', 
                                justifyContent: 'flex-end',
                                backgroundColor: 'white',
                                flexShrink: 0
                            }}>
                                <NJButton
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setEditingGroupId(null)}
                                >
                                    Annuler
                                </NJButton>
                                <NJButton
                                    type="submit"
                                    variant="primary"
                                >
                                    Valider
                                </NJButton>
                            </div>
                        </form>
                    </div>
                </>
            )}

            {/* Modale de suppression */}
            {deletingNodeId !== null && (
                <NJModal isOpen={true} onClose={() => setDeletingNodeId(null)} size="sm">
                    <div style={{ padding: '24px', textAlign: 'center' }}>
                        <div style={{ fontSize: '48px', color: 'var(--nj-semantic-color-text-danger-default)', marginBottom: '16px' }}>
                            ⓘ
                        </div>
                        <NJText variant="heading-medium" style={{ marginBottom: '12px', display: 'block' }}>
                            Confirmation de suppression
                        </NJText>
                        <NJText variant="body-default" style={{ marginBottom: '24px', display: 'block', color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                            Êtes-vous sûr de vouloir supprimer {deletingNode?.data.entityLabel?.toLowerCase()} {deletingNode?.data.label} ?
                        </NJText>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <NJButton
                                variant="secondary"
                                onClick={() => setDeletingNodeId(null)}
                            >
                                Annuler
                            </NJButton>
                            <NJButton
                                variant="primary"
                                onClick={handleDeleteNode}
                                disabled={deleteNodeMutation.isPending}
                            >
                                Je supprime
                            </NJButton>
                        </div>
                    </div>
                </NJModal>
            )}
        </div>
    );
}

// ── Layout automatique ──────────────────────────────────────────────────

function applyAutoLayout(nodes: Node<IBaseData>[], edges: Edge[]): Node<IBaseData>[] {
    // Créer une map des nœuds par ID pour accès rapide
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    
    // Calculer les niveaux (layers) pour chaque nœud basé sur les edges
    const levels = new Map<string, number>();
    const visited = new Set<string>();
    
    // Trouver les nœuds sources (sans edges entrants ou types spécifiques)
    const sourceNodes = nodes.filter(n => {
        const hasIncomingEdge = edges.some(e => e.target === n.id);
        const isSource = n.data.entityName === 'Return' || 
                        n.data.entityName === 'WaterCooling' || 
                        n.data.entityName === 'PowerContract';
        return !hasIncomingEdge || isSource;
    });
    
    // BFS pour assigner les niveaux
    const queue: Array<{ nodeId: string; level: number }> = sourceNodes.map(n => ({ nodeId: n.id, level: 0 }));
    
    while (queue.length > 0) {
        const { nodeId, level } = queue.shift()!;
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);
        levels.set(nodeId, level);
        
        // Trouver les nœuds enfants
        const outgoingEdges = edges.filter(e => e.source === nodeId);
        for (const edge of outgoingEdges) {
            if (!visited.has(edge.target)) {
                queue.push({ nodeId: edge.target, level: level + 1 });
            }
        }
    }
    
    // Grouper les nœuds par niveau
    const nodesByLevel = new Map<number, Node<IBaseData>[]>();
    for (const node of nodes) {
        if (node.parentId) continue; // Skip enfants de groupes
        const level = levels.get(node.id) ?? 0;
        if (!nodesByLevel.has(level)) {
            nodesByLevel.set(level, []);
        }
        nodesByLevel.get(level)!.push(node);
    }
    
    // Paramètres de layout
    const LEVEL_SPACING = 350; // Espacement horizontal entre niveaux
    const NODE_SPACING = 30; // Espacement vertical entre nœuds
    const START_X = 80;
    const START_Y = 80;
    
    // Positionner les nœuds
    const layoutedNodes = nodes.map(node => {
        if (node.parentId) {
            // Garder les positions relatives pour les enfants de groupes
            return node;
        }
        
        const level = levels.get(node.id) ?? 0;
        const nodesInLevel = nodesByLevel.get(level) ?? [];
        const indexInLevel = nodesInLevel.indexOf(node);
        
        const x = START_X + (level * LEVEL_SPACING);
        const y = START_Y + (indexInLevel * (200 + NODE_SPACING));
        
        return {
            ...node,
            position: { x, y },
        };
    });
    
    return layoutedNodes;
}

// ── Main Component ──────────────────────────────────────────────────────

export default function ColdWaterNetworkEditor() {
    const [activeTab, setActiveTab] = useState<TabKey>('editor');
    const [selectedPlantId, setSelectedPlantId] = useState<PlantId>('cber');
    
    // État pour les configurations de groupes (série vs parallèle)
    const [groupConfigs, setGroupConfigs] = useState<GroupConfigs>(() => {
        const storageKey = `cold-water-group-configs-${selectedPlantId}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.warn('Failed to load group configs', e);
            }
        }
        return {};
    });

    // Sauvegarder les configurations de groupes dans localStorage
    useEffect(() => {
        const storageKey = `cold-water-group-configs-${selectedPlantId}`;
        localStorage.setItem(storageKey, JSON.stringify(groupConfigs));
    }, [groupConfigs, selectedPlantId]);

    const handleGroupConfigChange = useCallback((groupId: string, config: CondenserCoolingType) => {
        setGroupConfigs((prev) => ({ ...prev, [groupId]: config }));
    }, []);

    // Générer le graphe en fonction de la centrale sélectionnée avec layout automatique
    const { nodes: initialNodes, edges: initialEdges } = useMemo(
        () => {
            const graph = buildGraphForPlant(selectedPlantId, groupConfigs);
            let layoutedNodes = applyAutoLayout(graph.nodes, graph.edges);
            
            // Restaurer les positions sauvegardées depuis localStorage
            const storageKey = `cold-water-editor-${selectedPlantId}`;
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                try {
                    const nodePositions = JSON.parse(saved) as Array<{ 
                        id: string; 
                        position: { x: number; y: number };
                        parentId?: string;
                    }>;
                    layoutedNodes = layoutedNodes.map((node) => {
                        const savedPos = nodePositions.find((p) => p.id === node.id);
                        if (savedPos) {
                            return { ...node, position: savedPos.position };
                        }
                        return node;
                    });
                } catch (e) {
                    console.warn('Failed to load saved positions', e);
                }
            }
            
            return { nodes: layoutedNodes, edges: graph.edges };
        },
        [selectedPlantId, groupConfigs]
    );

    return (
        <PrototypeStudioLayout title={meta.title}>
            <div
                style={{
                    margin: '0 calc(var(--nj-core-space-500) * -1) calc(var(--nj-core-space-500) * -1)',
                    fontFamily: 'var(--nj-core-font-family-sans)',
                    background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                    display: 'flex',
                    height: 'calc(100vh - 100px)',
                }}
            >
                {/* Sidebars gauche fixes */}
                <AppRail />
                <NetworkSidebar />

                {/* Zone principale avec tabs + contenu */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Tabs horizontaux en haut */}
                    <div
                        style={{
                            borderBottom: '1px solid var(--nj-core-color-grey-200)',
                            padding: '0 var(--nj-core-space-400)',
                            backgroundColor: 'white',
                        }}
                    >
                        <NJTabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
                            <NJTab value="editor" label="Éditeur de production" />
                            <NJTab value="contracts" label="Électricité auxiliaires" />
                            <NJTab value="synthesis" label="Synthèse" />
                        </NJTabs>
                    </div>

                    {/* Contenu de l'onglet */}
                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                        {activeTab === 'editor' && (
                            <ProductionEditorProvider
                                key={`${selectedPlantId}-${JSON.stringify(groupConfigs)}`}
                                initialNodes={initialNodes}
                                initialEdges={initialEdges}
                            >
                                <div style={{ flex: 1, position: 'relative' }}>
                                    <ColdWaterNetworkCanvas
                                        selectedPlantId={selectedPlantId}
                                        onPlantChange={setSelectedPlantId}
                                        groupConfigs={groupConfigs}
                                        onGroupConfigChange={handleGroupConfigChange}
                                    />
                                </div>
                            </ProductionEditorProvider>
                        )}

                        {activeTab === 'contracts' && (
                            <div style={{ flex: 1, padding: '48px 24px', textAlign: 'center' }}>
                                <NJText variant="heading-medium">Électricité auxiliaires</NJText>
                                <NJText
                                    variant="body-default"
                                    style={{ marginTop: '8px', color: 'var(--nj-core-color-grey-600)' }}
                                >
                                    Gestion des contrats d'électricité auxiliaires.
                                </NJText>
                            </div>
                        )}

                        {activeTab === 'synthesis' && (
                            <div style={{ flex: 1, padding: '48px 24px', textAlign: 'center' }}>
                                <NJText variant="heading-medium">Synthèse</NJText>
                                <NJText
                                    variant="body-default"
                                    style={{ marginTop: '8px', color: 'var(--nj-core-color-grey-600)' }}
                                >
                                    Vue synthétique du réseau d'eau froide.
                                </NJText>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <NJToastContainer />
        </PrototypeStudioLayout>
    );
}
